import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CbcConnectionInput, CbcConnectionTestResult } from "@/api/config-migration";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import {
  useCbcConnection,
  useSaveCbcConnection,
  useTestCbcConnection,
} from "@/hooks/useConfigMigration";
import { connectionSaveFailureText } from "@/lib/connection-save-failure";

/** Aba de conexão com o SAP CBC de quem está logado — usada pelo agente de
 * configuração para autenticar no CBC com a credencial de quem aperta o play. */
export function CbcConnectionTab({
  projectId,
  onSaved,
}: {
  projectId: string;
  /** Chamado após o save ter sucesso — usado pelo dialog do Nó 02 para fechar o modal. */
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useCbcConnection(projectId);
  const { mutate: save, isPending: isSaving } = useSaveCbcConnection(projectId);
  const { mutate: testConnection, isPending: isTesting } = useTestCbcConnection(projectId);

  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // O workspace não entra em `currentFields`: o teste só prova o login, e trocar o
  // workspace não invalida um teste que passou.
  const [workspace, setWorkspace] = useState("");
  const [testResult, setTestResult] = useState<CbcConnectionTestResult | null>(null);
  // Os valores do último teste que passou: o Save só libera para eles. A estrutura
  // organizacional só roda com a conexão testada, não só cadastrada.
  const [testedFields, setTestedFields] = useState<string | null>(null);
  // Save ou Test que falhou: o motivo fica no próprio formulário. No passo 1 da Execução
  // ele é a única saída da tela, e sem isto o 403 (papel abaixo de consultor) era mudo.
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setBaseUrl(data?.base_url ?? "");
    setUsername(data?.username ?? "");
    setWorkspace(data?.workspace ?? "");
    // A senha nunca volta da API — o campo nasce sempre vazio, mesmo configurado.
  }, [data?.base_url, data?.username, data?.workspace]);

  const configured = !!data?.configured;
  // Com a conexão já gravada, a senha em branco significa "manter a do cofre"
  // — ela nunca volta da API, e exigi-la travava Save e Test para quem só
  // queria trocar a URL/o usuário ou testar o que já estava gravado.
  const canSave =
    baseUrl.trim() !== "" && username.trim() !== "" && (configured || password.trim() !== "");

  // A URL entra sem a barra final, como o servidor a grava: o Test salva a conexão antes
  // de logar, a tela relê o cadastro e reescreve o campo com o texto gravado. Comparar o
  // texto digitado com o reescrito travava o Save para quem colou a URL com "/" no fim.
  const currentFields = JSON.stringify([
    baseUrl.trim().replace(/\/+$/, ""),
    username.trim(),
    password,
  ]);
  const testedOk = testResult?.success === true && testedFields === currentFields;

  function payload(): CbcConnectionInput {
    const body: CbcConnectionInput = { base_url: baseUrl.trim(), username: username.trim() };
    if (password.trim() !== "") body.password = password;
    // Sempre vai como texto: o vazio apaga o workspace gravado.
    body.workspace = workspace.trim();
    return body;
  }

  const handleSave = () => {
    if (!canSave || !testedOk) return;
    setActionError(null);
    save(payload(), {
      onSuccess: () => onSaved?.(),
      onError: (error) =>
        setActionError(connectionSaveFailureText(t, error, t("Could not save the CBC connection."))),
    });
  };

  const handleTest = () => {
    if (!canSave) return;
    setTestResult(null);
    setActionError(null);
    // Ao contrário do Save, o teste NUNCA fecha o modal sozinho: a pessoa
    // precisa ver o resultado antes de sair da tela.
    const fields = currentFields;
    testConnection(payload(), {
      onSuccess: (result) => {
        setTestResult(result);
        setTestedFields(result.success ? fields : null);
      },
      onError: (error) =>
        setActionError(connectionSaveFailureText(t, error, t("Could not test the CBC connection."))),
    });
  };

  if (isLoading) {
    return <Loader2 className="w-4 h-4 animate-spin" />;
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t("This connection is yours. No one else in the project sees or uses your user and password.")}
      </p>
      {configured && (
        <p className="text-xs text-muted-foreground">
          {t(
            "CBC connection already configured. The password is never shown again for security reasons; saving replaces the current credential.",
          )}
        </p>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="cbc-base-url">{t("CBC URL")}</Label>
        <Input
          id="cbc-base-url"
          placeholder="https://my16122536.prod01.cbc.na.one.cloud.sap"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cbc-username">{t("CBC user")}</Label>
        <Input
          id="cbc-username"
          placeholder="CBC_USER"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cbc-password">{t("CBC password")}</Label>
        <Input
          id="cbc-password"
          type="password"
          placeholder={configured ? "••••••••" : ""}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {t(
            configured
              ? "Never returned by the API; leave it blank to keep the current one, or type a new one to change it."
              : "Never returned by the API; type it again to change it.",
          )}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cbc-workspace">{t("CBC workspace")}</Label>
        <Input
          id="cbc-workspace"
          placeholder="myGROW - Customizing"
          value={workspace}
          onChange={(e) => setWorkspace(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "The exact name shown at the top of the CBC, with the same capitals. The agent switches to this workspace before the organizational structure; leave it blank to use the one already open.",
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <Button
          onClick={handleSave}
          disabled={!canSave || !testedOk || isSaving}
          title={testedOk ? undefined : t("Test the connection first; Save unlocks after a successful test.")}
        >
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("Save")}
        </Button>
        <Button variant="outline" onClick={handleTest} disabled={!canSave || isTesting}>
          {isTesting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t("Test connection")}
        </Button>
        {isTesting && (
          <Badge variant="secondary" role="status">
            {t("Connecting to CBC…")}
          </Badge>
        )}
      </div>
      {actionError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}
      {testResult && (
        <Alert
          variant={testResult.success ? "default" : "destructive"}
          className={
            testResult.success
              ? "border-green-600/40 bg-green-50 text-green-800 [&>svg]:text-green-600"
              : undefined
          }
        >
          {testResult.success ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <AlertDescription className={testResult.success ? "text-green-800" : undefined}>
            {testResult.message}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
